import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { ProjectManagementService } from '../project-management.service';
import { TaskCreateDialogComponent } from './task-create-dialog/task-create-dialog.component';

type TaskFilter = 'all' | 'standalone' | number;

@Component({
  selector: 'app-project-tasks',
  templateUrl: './project-tasks.component.html',
  styleUrls: ['./project-tasks.component.scss']
})
export class ProjectTasksComponent implements OnInit {
  loading = false;
  saving = false;
  commentSaving = false;
  projects: any[] = [];
  staffs: any[] = [];
  rows: any[] = [];
  selectedTask: any = null;
  previewMedia: any = null;
  commentText = '';
  commentParentId: number | null = null;
  commentAttachments: any[] = [];
  searchTerm = '';
  statusFilter = 'All';
  projectFilter: TaskFilter = 'all';
  taskMentionState = { visible: false, query: '', start: 0, end: 0 };
  commentMentionState = { visible: false, query: '', start: 0, end: 0 };

  statusOptions = ['Pending', 'In Progress', 'Blocked', 'Done'];
  priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

  form: any = {
    title: '',
    description: '',
    mediaAttachments: [],
    priority: 'Medium',
    status: 'Pending',
    progressPercent: 0,
    assignedStaffId: null,
    dueDate: ''
  };

  constructor(
    private projectService: ProjectManagementService,
    private auth: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadProjects();
    this.loadStaffs();
    this.loadTasks();
  }

  loadProjects(): void {
    this.projectService.getProjects().subscribe({
      next: (projects) => this.projects = projects || [],
      error: () => this.projects = []
    });
  }

  loadStaffs(): void {
    this.projectService.getStaffs().subscribe({
      next: (staffs) => this.staffs = staffs || [],
      error: () => this.staffs = []
    });
  }

  loadTasks(selectedTaskId?: number | null): void {
    this.loading = true;
    this.projectService.getTaskWorkspace().subscribe({
      next: (rows) => {
        this.rows = rows || [];
        const taskId = selectedTaskId ?? this.selectedTask?.id ?? null;
        if (taskId) {
          const matched = this.findTaskById(taskId, this.rows);
          if (matched) {
            this.editTask(matched);
          } else {
            this.resetForm();
          }
        } else if (this.selectedTask && !this.findTaskById(this.selectedTask.id, this.rows)) {
          this.resetForm();
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load task workspace.', 'error');
      }
    });
  }

  onWorkspaceFilterChange(): void {
    if (this.selectedTask && !this.findTaskById(this.selectedTask.id, this.filteredRows)) {
      this.resetForm();
    }
  }

  openCreateTaskModal(parentTask: any | null = null): void {
    if (!this.canCreateTask()) return;

    const dialogRef = this.dialog.open(TaskCreateDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      panelClass: ['app-modal-panel', 'task-create-panel'],
      backdropClass: 'app-modal-backdrop',
      data: {
        projects: this.projects,
        staffs: this.staffs,
        topLevelTasks: this.rows,
        projectFilter: this.projectFilter,
        presetParentTask: parentTask,
        canAssignAnyStaff: this.canAssignAnyStaff(),
        currentStaffId: this.currentStaffId
      }
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) return;

      this.saving = true;
      this.projectService.createTask(payload).subscribe({
        next: (createdTask) => {
          this.saving = false;
          this.loadTasks(createdTask?.id || null);
          Swal.fire('Success', parentTask ? 'Subtask created successfully.' : 'Task created successfully.', 'success');
        },
        error: (err) => {
          this.saving = false;
          Swal.fire('Error', err?.error?.message || 'Failed to create task.', 'error');
        }
      });
    });
  }

  editTask(row: any): void {
    this.selectedTask = row;
    this.commentText = '';
    this.commentParentId = null;
    this.commentAttachments = [];
    this.form = {
      title: row?.title || '',
      description: row?.description || '',
      mediaAttachments: Array.isArray(row?.mediaAttachments) ? [...row.mediaAttachments] : [],
      priority: row?.priority || 'Medium',
      status: row?.status || 'Pending',
      progressPercent: Number(row?.progressPercent ?? 0),
      assignedStaffId: row?.assignedStaffId || null,
      dueDate: this.toInputDate(row?.dueDate)
    };
  }

  resetForm(): void {
    this.selectedTask = null;
    this.commentText = '';
    this.commentParentId = null;
    this.commentAttachments = [];
    this.form = {
      title: '',
      description: '',
      mediaAttachments: [],
      priority: 'Medium',
      status: 'Pending',
      progressPercent: 0,
      assignedStaffId: null,
      dueDate: ''
    };
  }

  saveTask(): void {
    if (!this.selectedTask?.id) {
      this.openCreateTaskModal();
      return;
    }
    if (!this.form.title?.trim()) {
      Swal.fire('Validation', 'Task title is required.', 'warning');
      return;
    }
    if (!this.canEditTasks()) return;

    const payload: any = {
      title: this.form.title?.trim(),
      description: this.form.description || null,
      mediaAttachments: this.form.mediaAttachments || [],
      mentionedStaffIds: this.extractMentionedStaffIds(this.form.description),
      priority: this.form.priority || 'Medium',
      status: this.form.status || 'Pending',
      assignedStaffId: this.canAssignAnyStaff() ? (this.form.assignedStaffId || null) : (this.currentStaffId || null),
      dueDate: this.form.dueDate || null
    };

    if (!this.taskHasSubtasks(this.selectedTask)) {
      payload.progressPercent = Number(this.form.progressPercent || 0);
    }

    if (!this.canEditDescription(this.selectedTask)) {
      payload.description = this.selectedTask.description || null;
    }

    this.saving = true;
    this.projectService.updateProjectTask(this.selectedTask.id, payload).subscribe({
      next: () => {
        this.saving = false;
        Swal.fire('Success', 'Task saved successfully.', 'success');
        this.loadTasks(this.selectedTask.id);
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save task.', 'error');
      }
    });
  }

  addComment(): void {
    if (!this.selectedTask?.id) return;
    const comment = (this.commentText || '').trim();
    if (!comment) {
      Swal.fire('Validation', 'Enter an update before posting.', 'warning');
      return;
    }

    this.commentSaving = true;
    this.projectService.addTaskComment(this.selectedTask.id, {
      comment,
      parentCommentId: this.commentParentId,
      mediaAttachments: this.commentAttachments,
      mentionedStaffIds: this.extractMentionedStaffIds(this.commentText)
    }).subscribe({
      next: () => {
        this.commentSaving = false;
        this.commentText = '';
        this.commentParentId = null;
        this.commentAttachments = [];
        this.loadTasks(this.selectedTask.id);
      },
      error: (err) => {
        this.commentSaving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to post task update.', 'error');
      }
    });
  }

  setReplyTarget(comment: any | null): void {
    this.commentParentId = comment?.id || null;
  }

  async onTaskMediaSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    await this.appendTaskMediaFiles(files);
    input.value = '';
  }

  async onTaskMediaDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    await this.appendTaskMediaFiles(files);
  }

  private async appendTaskMediaFiles(files: File[]): Promise<void> {
    try {
      const attachments = await Promise.all(files.map((file) => this.readFile(file)));
      this.form.mediaAttachments = [...(this.form.mediaAttachments || []), ...attachments];
    } catch {
      Swal.fire('Error', 'Failed to read one or more task files.', 'error');
    }
  }

  removeTaskMedia(index: number): void {
    this.form.mediaAttachments = (this.form.mediaAttachments || []).filter((_: any, i: number) => i !== index);
  }

  async onCommentMediaSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    await this.appendCommentMediaFiles(files);
    input.value = '';
  }

  async onCommentMediaDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    await this.appendCommentMediaFiles(files);
  }

  private async appendCommentMediaFiles(files: File[]): Promise<void> {
    try {
      const attachments = await Promise.all(files.map((file) => this.readFile(file)));
      this.commentAttachments = [...this.commentAttachments, ...attachments];
    } catch {
      Swal.fire('Error', 'Failed to read one or more comment files.', 'error');
    }
  }

  removeCommentMedia(index: number): void {
    this.commentAttachments = this.commentAttachments.filter((_: any, i: number) => i !== index);
  }

  onTaskDescriptionInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.updateMentionState(textarea.value, textarea.selectionStart || 0, this.taskMentionState);
  }

  onCommentInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.updateMentionState(textarea.value, textarea.selectionStart || 0, this.commentMentionState);
  }

  insertTaskMention(staff: any, textarea: HTMLTextAreaElement): void {
    this.form.description = this.insertMention(this.form.description, staff?.fullName || '', this.taskMentionState);
    this.taskMentionState.visible = false;
    textarea.focus();
  }

  insertCommentMention(staff: any, textarea: HTMLTextAreaElement): void {
    this.commentText = this.insertMention(this.commentText, staff?.fullName || '', this.commentMentionState);
    this.commentMentionState.visible = false;
    textarea.focus();
  }

  canCreateTask(): boolean {
    return this.auth.isAdmin()
      || this.auth.hasPermission('project.view')
      || this.auth.hasPermission('project.task.manage')
      || this.auth.hasPermission('project.team.manage')
      || this.auth.hasPermission('project.manage');
  }

  canEditTasks(): boolean {
    return this.auth.isAdmin()
      || this.auth.hasPermission('project.task.manage')
      || this.auth.hasPermission('project.manage');
  }

  canAssignAnyStaff(): boolean {
    return this.auth.isAdmin()
      || this.auth.hasPermission('project.task.manage')
      || this.auth.hasPermission('project.team.manage')
      || this.auth.hasPermission('project.manage');
  }

  canEditDescription(task: any = this.selectedTask): boolean {
    if (!task) return true;
    if (this.auth.isAdmin()) return true;
    const currentUserId = this.auth.getUser()?.userId || '';
    return !!currentUserId && currentUserId === task.createdByUserId;
  }

  get workspaceLabel(): string {
    if (this.projectFilter === 'all') return 'Task workspace';
    if (this.projectFilter === 'standalone') return 'Standalone tasks';
    return this.projects.find((project) => project.id === this.projectFilter)?.name || 'Task workspace';
  }

  get filteredRows(): any[] {
    const query = String(this.searchTerm || '').trim().toLowerCase();
    return (this.rows || []).filter((row) => {
      const matchesProject = this.projectFilter === 'all'
        || (this.projectFilter === 'standalone' ? !row.projectId : row.projectId === this.projectFilter);
      const matchesStatus = this.statusFilter === 'All' || row.status === this.statusFilter;
      if (!matchesProject || !matchesStatus) return false;
      if (!query) return true;

      const haystack = [
        row.title,
        row.description,
        row.status,
        row.priority,
        row.assignedStaffName,
        row.createdByName,
        row.projectName
      ].join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }

  get totalTasks(): number {
    return this.filteredRows.length;
  }

  get completedTasks(): number {
    return this.filteredRows.filter((row) => this.isDone(row)).length;
  }

  get openTasks(): number {
    return this.filteredRows.filter((row) => !this.isDone(row)).length;
  }

  get blockedTasks(): number {
    return this.filteredRows.filter((row) => String(row.status || '').toLowerCase() === 'blocked').length;
  }

  get overdueTasks(): number {
    return this.filteredRows.filter((row) => this.isOverdue(row)).length;
  }

  get currentStaffId(): number | null {
    const currentUserId = this.auth.getUser()?.userId || '';
    const currentEmail = this.auth.getUser()?.email || '';
    const match = this.staffs.find((staff) =>
      (!!currentUserId && staff.userId === currentUserId) ||
      (!!currentEmail && String(staff.email || '').toLowerCase() === currentEmail.toLowerCase())
    );
    return match?.id || null;
  }

  get selectedSubtasks(): any[] {
    return Array.isArray(this.selectedTask?.subtasks) ? this.selectedTask.subtasks : [];
  }

  taskHasSubtasks(task: any = this.selectedTask): boolean {
    return (task?.subtaskCount || 0) > 0 || (task?.subtasks?.length || 0) > 0;
  }

  mediaKind(url: string, contentType?: string): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
    const type = String(contentType || '').toLowerCase();
    const source = String(url || '').toLowerCase();
    if (type.startsWith('image/') || source.startsWith('data:image/')) return 'image';
    if (type.startsWith('video/') || source.startsWith('data:video/')) return 'video';
    if (type.startsWith('audio/') || source.startsWith('data:audio/')) return 'audio';
    if (type === 'application/pdf' || source.startsWith('data:application/pdf')) return 'pdf';
    return 'file';
  }

  openPreview(media: any): void {
    this.previewMedia = media || null;
  }

  closePreview(): void {
    this.previewMedia = null;
  }

  taskMentionSuggestions(): any[] {
    return this.getMentionSuggestions(this.taskMentionState);
  }

  commentMentionSuggestions(): any[] {
    return this.getMentionSuggestions(this.commentMentionState);
  }

  statusClass(status: string | null | undefined): string {
    const key = (status || '').toLowerCase().replace(/\s+/g, '-');
    return `status-${key}`;
  }

  priorityClass(priority: string | null | undefined): string {
    const key = (priority || '').toLowerCase();
    return `priority-${key}`;
  }

  completionTone(row: any): string {
    if (this.isDone(row)) return 'done';
    if (this.isOverdue(row)) return 'overdue';
    if (String(row?.status || '').toLowerCase() === 'blocked') return 'blocked';
    return 'active';
  }

  progressLabel(row: any): string {
    const progress = Number(row?.progressPercent ?? 0);
    if (this.isDone(row)) return 'Completed';
    if (progress >= 80) return 'Closing in';
    if (progress >= 40) return 'In motion';
    if (progress > 0) return 'Started';
    return 'Not started';
  }

  dueState(row: any): string {
    if (!row?.dueDate) return 'No due date';
    if (this.isDone(row)) return 'Closed';
    if (this.isOverdue(row)) return 'Overdue';

    const due = new Date(row.dueDate).getTime();
    const now = Date.now();
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 2) return 'Due soon';
    return 'On track';
  }

  isOverdue(row: any): boolean {
    if (!row?.dueDate || this.isDone(row)) return false;
    const due = new Date(row.dueDate);
    return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  isDone(row: any): boolean {
    const status = String(row?.status || '').toLowerCase();
    return status === 'done' || status === 'completed';
  }

  private findTaskById(taskId: number, rows: any[]): any | null {
    for (const row of rows || []) {
      if (row.id === taskId) return row;
      const foundChild = this.findTaskById(taskId, row.subtasks || []);
      if (foundChild) return foundChild;
    }
    return null;
  }

  private toInputDate(value: string | Date | null | undefined): string {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = `${dt.getMonth() + 1}`.padStart(2, '0');
    const d = `${dt.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private extractMentionedStaffIds(text: string): number[] {
    const value = String(text || '');
    return this.staffs
      .filter((staff) => value.includes(`@${staff.fullName}`))
      .map((staff) => staff.id);
  }

  private updateMentionState(text: string, caret: number, state: { visible: boolean; query: string; start: number; end: number }): void {
    const value = String(text || '');
    const left = value.slice(0, caret);
    const match = left.match(/(^|\s)@([^\s@]*)$/);
    if (!match) {
      state.visible = false;
      state.query = '';
      return;
    }

    state.visible = true;
    state.query = (match[2] || '').toLowerCase();
    state.start = caret - match[2].length - 1;
    state.end = caret;
  }

  private getMentionSuggestions(state: { visible: boolean; query: string }): any[] {
    if (!state.visible) return [];
    return this.staffs
      .filter((staff) => !state.query || String(staff.fullName || '').toLowerCase().includes(state.query))
      .slice(0, 6);
  }

  private insertMention(text: string, fullName: string, state: { start: number; end: number }): string {
    const value = String(text || '');
    const before = value.slice(0, state.start);
    const after = value.slice(state.end);
    return `${before}@${fullName} ${after}`.replace(/\s{2,}/g, ' ');
  }

  private readFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        fileName: file.name,
        url: reader.result as string,
        contentType: file.type || 'application/octet-stream'
      });
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
